/**
 * Per-mode drill selector. URL: /drills/:mode
 *
 * Renders a "Surprise me" card with the mode's surpriseKickoff at the
 * top, followed by curated drill options. Spot-producing modes launch the
 * focused DrillView (/drill/new); conversational modes (Concept Deep Dive)
 * open the chat thread — the split is owned by `drillPathForMode`.
 */

import { Navigate, useParams } from "react-router-dom";
import { BackLink } from "@/shared/components/BackLink";

import { drillPathForMode } from "@/shared/chatRoute";
import { DrillLaunchLink } from "@/shared/components/DrillLaunchLink";
import {
  ChevronRight,
  ICON_STROKE,
  MODE_ICONS,
  Shuffle,
} from "@/shared/components/icons";
import {
  SESSION_MODES,
  VARIETY_DRILL_TARGET,
  type DrillOption,
  type SessionMode,
} from "@/shared/models";

export function DrillSelector() {
  const { mode } = useParams<{ mode: string }>();
  if (!mode || !(mode in SESSION_MODES)) {
    return <Navigate to="/drills" replace />;
  }
  const sessionMode = mode as SessionMode;
  const info = SESSION_MODES[sessionMode];
  const Icon = MODE_ICONS[sessionMode];

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-8 space-y-6">
      <header>
        <BackLink to="/drills" label="Back to modes" />
        <h1 className="text-2xl font-semibold text-text mt-2 flex items-center gap-3 tracking-tight">
          <span className="w-10 h-10 rounded-xl bg-accent/15 text-accent flex items-center justify-center border border-accent/15">
            <Icon size={22} strokeWidth={ICON_STROKE} />
          </span>
          {info.title}
        </h1>
        <p className="text-sm text-text-muted mt-1">{info.description}</p>
      </header>

      <SurpriseMeCard mode={sessionMode} kickoff={info.surpriseKickoff} />

      {info.options.length > 0 && (
        <section>
          <h2 className="text-[10px] font-semibold uppercase tracking-[0.15em] text-text-muted/80 mb-3">
            Or pick one
          </h2>
          <div className="space-y-2">
            {info.options.map((option, idx) => (
              <OptionRow key={idx} mode={sessionMode} option={option} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function SurpriseMeCard({
  mode,
  kickoff,
}: {
  mode: SessionMode;
  kickoff: string;
}) {
  // Surprise Me is always a variety drill — exercises whatever the LLM
  // picks across the mode's pool. drill_target = "variety" gives library
  // lookup a clean handle even though no specific skill was named.
  // Concept Deep Dive isn't a drill (no spots), but the variety target
  // is harmless there — the backend just won't try a library hit since
  // the mode isn't spot-producing.
  const params = new URLSearchParams({
    mode,
    kickoff,
    drill_target: VARIETY_DRILL_TARGET,
    drill_difficulty: "default",
  });
  return (
    <DrillLaunchLink
      to={`${drillPathForMode(mode)}?${params.toString()}`}
      className="block rounded-xl border border-accent/30 bg-accent/10 p-4 hover:border-accent/60 hover:bg-accent/15 active:border-accent/70 active:bg-accent/20 transition group"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center text-white shadow-[0_8px_20px_-8px_rgba(124,147,241,0.6)]">
          <Shuffle size={18} strokeWidth={ICON_STROKE} />
        </div>
        <div className="flex-1">
          <div className="font-semibold text-text tracking-tight">Surprise me</div>
          <div className="text-xs text-text-muted">
            I'll pick the scenario.
          </div>
        </div>
        <ChevronRight
          size={16}
          strokeWidth={ICON_STROKE}
          className="text-text-muted/60 group-hover:text-accent group-hover:translate-x-0.5 transition"
        />
      </div>
    </DrillLaunchLink>
  );
}

function OptionRow({
  mode,
  option,
}: {
  mode: SessionMode;
  option: DrillOption;
}) {
  const params = new URLSearchParams({ mode, kickoff: option.kickoff });
  if (option.drillTarget) params.set("drill_target", option.drillTarget);
  if (option.drillDifficulty)
    params.set("drill_difficulty", option.drillDifficulty);
  return (
    <DrillLaunchLink
      to={`${drillPathForMode(mode)}?${params.toString()}`}
      className="block rounded-xl border border-border bg-surface p-4 hover:border-accent/50 active:border-accent/60 active:bg-surface/80 transition group"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-text group-hover:text-accent transition">
            {option.label}
          </div>
          {option.subtitle && (
            <div className="text-xs text-text-muted mt-1">{option.subtitle}</div>
          )}
        </div>
        <ChevronRight
          size={14}
          strokeWidth={ICON_STROKE}
          className="text-text-muted/60 group-hover:text-accent group-hover:translate-x-0.5 transition flex-shrink-0"
        />
      </div>
    </DrillLaunchLink>
  );
}
